<script lang="ts" setup>
import { SancareOcticon } from '@sancare/typescript-commons'
import { HealthDataDocument } from '@sancare/typescript-commons/src/types/health-data'
import { computed, ref } from 'vue'

import { SuggestionJustification } from '@/misc/types'
import JustificationSummary from '@/stay-displayer/diagnosis-lists/ApiJustificationSummary.vue'
import SvgSearchIcon from '@/stay-displayer/diagnosis-lists/SvgSearchIcon.vue'
import { MedicalActSuggestion } from '@/stay-displayer/medical-act/types'
import { useStore } from '@/store'
import { PredictedLabelType } from '@/store/modules/diagnosis/types'
import { StayHealthData } from '@/store/modules/stay/types'

const store = useStore()

const emit = defineEmits<{
  (e: 'promote-medical-act', suggestion: MedicalActSuggestion): void
}>()

const props = defineProps<{
  healthData: StayHealthData
  suggestion: MedicalActSuggestion
  suggestionJustifications?: Record<string, SuggestionJustification> | null
}>()

const showJustificationSummary = ref(false)

const searchNames = computed(() => props.suggestion.searches.map((sss) => sss.search.name).join(', '))

const apiJustification = computed(() => {
  return props.suggestionJustifications?.[`${PredictedLabelType.MEDICAL_ACTS}:${props.suggestion.reference}`] ?? null
})

function makeSelection() {
  store.commit('smrStay/setJustificationSelection', {
    labelType: PredictedLabelType.MEDICAL_ACTS,
    labelReference: props.suggestion.reference,
    savedSearchIds: props.suggestion.searches.map((sss) => sss.search.id),
  })
}

function handleHealthDataSelection(mainContent: HealthDataDocument) {
  store.commit('smrStay/setJustificationShouldScroll', true)
  store.commit('smrStay/setMainContent', mainContent)
  makeSelection()
  showJustificationSummary.value = false
}

function onShow() {
  showJustificationSummary.value = true
  makeSelection()
}

function flagJustification(payload) {
  store.dispatch('smrStay/createFalseJustificationForSuggestion', payload)
}

function unflagJustification({ justificationId, docType, docId }) {
  store.dispatch('smrStay/deleteFalseJustification', {
    justificationId,
    reportId: docType === 'report' ? docId : null,
    healthEntryId: docType === 'textualHealthEntry' ? docId : null,
  })
}
</script>

<template>
  <div class="d-flex align-items-center w-100 gap-2">
    <span
      v-tooltip="$t('medicalAct.promote')"
      class="flex-shrink-0 cp"
      @click="emit('promote-medical-act', suggestion)"
    >
      <sancare-octicon
        name="arrow-left"
        :width="16"
        :height="16"
      />
    </span>
    <v-dropdown
      :shown="showJustificationSummary"
      placement="left"
      class="popover-container flex-grow-1 min-w-0"
      popover-class="justification-popover"
      @show="onShow"
      @auto-hide="showJustificationSummary = false"
    >
      <div
        class="d-flex align-items-center justify-content-between gap-2"
      >
        <div class="min-w-0">
          <strong>{{ suggestion.reference }}</strong>
        </div>
        <div class="d-flex align-items-center gap-1 flex-shrink-0">
          <div
            v-if="suggestion.searches.length"
            v-tooltip.left="{
              content: searchNames,
              delay: 0
            }"
            class="d-flex align-items-center search-info"
          >
            <sancare-octicon
              name="search"
              :width="13"
              :height="13"
              class="me-1"
            />
            <small
              class="text-truncate"
              style="max-width: 80px;"
            >
              {{ searchNames }}
            </small>
          </div>
          <span v-if="suggestion.prediction">
            <sancare-octicon
              name="robot"
              :width="16"
            />
            <small class="ms-1">{{ Math.round(suggestion.prediction.confidence * 100) }}%</small>
          </span>
        </div>
      </div>
      <template #popper>
        <justification-summary
          :is-loading="false"
          :justification="apiJustification"
          :health-data="healthData"
          :label-type="PredictedLabelType.MEDICAL_ACTS"
          :label-reference="suggestion.reference"
          :no-value-label="$t('justification.noValue')"
          @select-health-data="handleHealthDataSelection"
          @flag-justification="flagJustification"
          @unflag-justification="unflagJustification"
        />
      </template>
    </v-dropdown>
  </div>
</template>
